import React, { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts'

type ModelBreakdown = Record<string, number>

interface ModelBarChartProps {
  data?: ModelBreakdown
  isLoading?: boolean
  error?: string | null
}

/**
 * ModelBarChart displays the breakdown of usage by model type (Opus, Haiku, Max)
 * Heights proportional to usage counts
 * Uses Puzzle brand color #FF5433 for primary bars
 */
// Color palette for model bars (module-level to avoid useMemo dep warning)
const MODEL_COLORS: Record<string, string> = {
  opus: '#FF5433',
  haiku: '#FFA31A',
  sonnet: '#B6E5F5',
  max: '#A3F5D7',
}
const DEFAULT_COLOR = '#999999'

const ModelBarChart: React.FC<ModelBarChartProps> = ({ data, isLoading = false, error = null }) => {
  // Transform model breakdown object into chart data
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return []
    return Object.entries(data)
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([model, count]) => ({
        name: model.charAt(0).toUpperCase() + model.slice(1),
        usage: count,
        fill: MODEL_COLORS[model] || DEFAULT_COLOR,
      }))
  }, [data])

  // Loading state
  if (isLoading) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#F3F3F3] rounded-puzzle"
        role="status"
        aria-label="Loading chart"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#FF5433] border-t-transparent mx-auto mb-2" />
          <p className="text-[#666666] text-sm">Loading chart...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#FFEBEB] rounded-puzzle border border-[#D63329]"
        role="alert"
      >
        <div className="text-center">
          <p className="text-[#D63329] text-sm font-bold">Error loading chart</p>
          <p className="text-[#666666] text-xs mt-1">{error}</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (chartData.every((item) => item.usage === 0)) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#F3F3F3] rounded-puzzle"
        role="status"
      >
        <p className="text-[#999999] text-sm">No data available</p>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#DCDCDC" />
          <XAxis
            dataKey="name"
            tick={{ fill: '#666666', fontSize: 12 }}
            axisLine={{ stroke: '#DCDCDC' }}
          />
          <YAxis
            tick={{ fill: '#666666', fontSize: 12 }}
            axisLine={{ stroke: '#DCDCDC' }}
            label={{ value: 'Usage Count', angle: -90, position: 'insideLeft', offset: 10 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #DCDCDC',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            labelStyle={{ color: '#000000' }}
            formatter={(value: unknown) => {
              if (typeof value === 'number') {
                return value.toLocaleString() as React.ReactNode
              }
              return String(value)
            }}
          />
          <Legend wrapperStyle={{ paddingTop: '16px' }} />
          <Bar dataKey="usage" name="Usage Count" isAnimationActive={true} radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default ModelBarChart
